'use client';

import { useState } from 'react';
import { MapPin, Mail, Phone, Clock } from 'lucide-react';
import axios from 'axios';
import { useLanguage } from '@/context/LanguageContext';
import { useTranslation } from '@/translations';
import { useStoreStatus } from '@/hooks/useStoreStatus';
import { DEFAULT_SCHEDULE, formatScheduleLines } from '@/lib/storeSchedule';

type FormData = {
  name: string;
  email: string;
  subject: string;
  message: string;
};

type FormErrors = {
  name?: string;
  email?: string;
  subject?: string;
  message?: string;
};

export default function ContactPageClient() {
  const { language } = useLanguage();
  const t = useTranslation(language);
  const { status: storeStatus } = useStoreStatus();
  const scheduleLines = formatScheduleLines(storeStatus?.schedule ?? DEFAULT_SCHEDULE, language);
  
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{ success?: boolean; message: string } | null>(null);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = t('contact.nameRequired');
    }
    
    if (!formData.email) {
      newErrors.email = t('contact.emailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('contact.emailInvalid');
    }
    
    if (!formData.subject.trim()) {
      newErrors.subject = t('contact.subjectRequired');
    }
    
    if (!formData.message.trim()) {
      newErrors.message = t('contact.messageRequired');
    } else if (formData.message.trim().length < 10) {
      newErrors.message = t('contact.messageMinLength');
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [name]: undefined
      }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setSubmitStatus(null);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Replace with actual API call
      const response = await axios.post('/api/contact', formData);
      const data = response.data;
      
      setSubmitStatus({
        success: true,
        message: data.message || t('contact.successMessage')
      });
      
      // Reset form on success
      setFormData({
        name: '',
        email: '',
        subject: '',
        message: ''
      });
    } catch (error) {
      console.error('Error submitting form:', error);
      setSubmitStatus({
        success: false,
        message: t('contact.errorMessage')
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <main className="organic min-h-screen">
      {/* Hero */}
      <section
        className="relative overflow-hidden"
        style={{ padding: 'calc(64px + var(--space-8) * 1.4) clamp(20px, 4vw, 48px) var(--space-6)' }}
      >
        <div
          aria-hidden="true"
          className="absolute rounded-full"
          style={{ left: -100, top: -100, width: 260, height: 260, background: 'var(--color-accent-200)', zIndex: 0 }}
        />
        <div className="max-w-7xl mx-auto relative" style={{ zIndex: 1, maxWidth: 640 }}>
          <span
            className="block uppercase mb-2"
            style={{ fontSize: 13, letterSpacing: '0.06em', fontWeight: 600, color: 'var(--color-accent-700)' }}
          >
            Suntem aproape
          </span>
          <h1 className="mb-3" style={{ fontSize: 'clamp(32px, 4vw, 48px)', lineHeight: 1.08, margin: '0 0 var(--space-3)' }}>
            {t('contact.title')}
          </h1>
          <p
            style={{
              fontSize: 16.5, lineHeight: 1.6,
              color: 'color-mix(in srgb, var(--color-text) 78%, transparent)',
              maxWidth: '52ch', margin: 0,
            }}
          >
            {t('contact.subtitle')}
          </p>
        </div>
      </section>

      {/* Main content */}
      <div
        className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-[1.2fr_1fr]"
        style={{ padding: '0 clamp(20px, 4vw, 48px) calc(var(--space-8) * 2)', gap: 'var(--space-8)' }}
      >
        {/* Contact form */}
        <div className="card elev-sm" style={{ padding: 'var(--space-6)' }}>
          <h2 style={{ margin: '0 0 var(--space-4)', fontSize: 22 }}>{t('contact.formTitle')}</h2>

          {submitStatus && (
            <div
              className="card"
              style={{
                background: submitStatus.success ? 'var(--color-accent-2-100)' : 'var(--color-accent-100)',
                padding: 'var(--space-3) var(--space-4)', marginBottom: 'var(--space-4)',
              }}
            >
              <p style={{ margin: 0, fontWeight: 600, color: submitStatus.success ? 'var(--color-accent-2-700)' : 'var(--color-accent-700)' }}>
                {submitStatus.message}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
            <div className="grid grid-cols-1 md:grid-cols-2" style={{ gap: 'var(--space-3)' }}>
              <div className="field">
                <label htmlFor="name">{t('contact.nameLabel')} *</label>
                <input
                  type="text"
                  id="name"
                  name="name"
                  className="input"
                  value={formData.name}
                  onChange={handleChange}
                  style={errors.name ? { borderColor: 'var(--color-accent-600)' } : undefined}
                />
                {errors.name && <p style={{ marginTop: 4, fontSize: 12.5, color: 'var(--color-accent-700)' }}>{errors.name}</p>}
              </div>
              <div className="field">
                <label htmlFor="email">{t('contact.emailLabel')} *</label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  className="input"
                  value={formData.email}
                  onChange={handleChange}
                  style={errors.email ? { borderColor: 'var(--color-accent-600)' } : undefined}
                />
                {errors.email && <p style={{ marginTop: 4, fontSize: 12.5, color: 'var(--color-accent-700)' }}>{errors.email}</p>}
              </div>
            </div>

            <div className="field">
              <label htmlFor="subject">{t('contact.subjectLabel')} *</label>
              <input
                type="text"
                id="subject"
                name="subject"
                className="input"
                value={formData.subject}
                onChange={handleChange}
                style={errors.subject ? { borderColor: 'var(--color-accent-600)' } : undefined}
              />
              {errors.subject && <p style={{ marginTop: 4, fontSize: 12.5, color: 'var(--color-accent-700)' }}>{errors.subject}</p>}
            </div>

            <div className="field">
              <label htmlFor="message">{t('contact.messageLabel')} *</label>
              <textarea
                id="message"
                name="message"
                rows={5}
                className="input"
                style={{ fontFamily: 'var(--font-body)', ...(errors.message ? { borderColor: 'var(--color-accent-600)' } : {}) }}
                value={formData.message}
                onChange={handleChange}
              />
              {errors.message && <p style={{ marginTop: 4, fontSize: 12.5, color: 'var(--color-accent-700)' }}>{errors.message}</p>}
            </div>

            <button type="submit" disabled={isSubmitting} className="btn btn-primary btn-block disabled:opacity-60 disabled:cursor-not-allowed">
              {isSubmitting ? t('contact.sending') : t('contact.sendButton')}
            </button>
          </form>
        </div>

        {/* Contact info + map */}
        <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
          <div className="card elev-sm" style={{ padding: 'var(--space-6)' }}>
            <h2 style={{ margin: '0 0 var(--space-4)', fontSize: 20 }}>{t('contact.contactInfo')}</h2>
            <div className="flex flex-col" style={{ gap: 'var(--space-4)' }}>
              <div className="flex items-start" style={{ gap: 'var(--space-3)' }}>
                <MapPin className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)' }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>{t('contact.locationTitle')}</p>
                  <p style={{ margin: '2px 0 0', color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', fontSize: 14 }}>Str. Unirii 240</p>
                  <p style={{ margin: 0, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', fontSize: 14 }}>Tamaseni, Neamț, România</p>
                </div>
              </div>

              <div className="flex items-start" style={{ gap: 'var(--space-3)' }}>
                <Mail className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)' }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>{t('contact.emailTitle')}</p>
                  <p style={{ margin: '2px 0 0', fontSize: 14 }}><a href="mailto:laurasimona97@yahoo.com">laurasimona97@yahoo.com</a></p>
                  <p style={{ margin: 0, fontSize: 14 }}><a href="mailto:simonabuzau2@gmail.com">simonabuzau2@gmail.com</a></p>
                </div>
              </div>

              <div className="flex items-start" style={{ gap: 'var(--space-3)' }}>
                <Phone className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)' }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>{t('contact.phoneTitle')}</p>
                  <p style={{ margin: '2px 0 0', color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', fontSize: 14 }}>0769 141 250</p>
                  <p style={{ margin: 0, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', fontSize: 14 }}>{t('contact.phoneSchedule')}</p>
                </div>
              </div>

              <div className="flex items-start" style={{ gap: 'var(--space-3)' }}>
                <Clock className="h-5 w-5 flex-shrink-0" strokeWidth={2.75} style={{ color: 'var(--color-accent-700)' }} />
                <div>
                  <p style={{ margin: 0, fontWeight: 600 }}>{t('contact.scheduleTitle')}</p>
                  {scheduleLines.map((line, i) => (
                    <p key={line} style={{ margin: i === 0 ? '2px 0 0' : 0, color: 'color-mix(in srgb, var(--color-text) 65%, transparent)', fontSize: 14 }}>{line}</p>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Google Maps — kept interactive per product decision, restyled container */}
          <div className="card elev-sm" style={{ padding: 0, overflow: 'hidden' }}>
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m17!1m12!1m3!1d2723.4448476744797!2d26.952465776893473!3d47.00269067115149!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m2!1m1!2zNDfCsDAwJzA5LjciTiAyNsKwNTcnMTYuOCJF!5e0!3m2!1sen!2sro!4v1737748800000!5m2!1sen!2sro"
              width="100%"
              height="360"
              style={{ border: 0, display: 'block' }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </div>
    </main>
  );
}
